import { Component, PLATFORM_ID, Renderer2, inject, computed, effect, OnInit, afterNextRender, signal, DOCUMENT } from '@angular/core';

import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { Router, NavigationEnd, RouterOutlet, NavigationStart, NavigationCancel, NavigationError, Scroll } from '@angular/router';
import { Store } from '@ngrx/store';
import { FromDictionaryPipe, getDictionaryLoaded } from '@teamfoster/sdk/dictionary-ngrx';

import * as navActions from '../../store/actions/nav.action';
import * as navSelectors from '../../store/selectors/nav.selector';
import { filter, tap } from 'rxjs/operators';
import { fade } from '../../animations';
import { ChangeDetectionStrategy } from '@angular/core';
import { CookieConfigService, CookieNoticeComponent, getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { getRouterState } from '../../store';
import { FromMenuPipe, getMenuLoaded } from '@teamfoster/sdk/menu-ngrx';

import { getTheme } from '../../store/selectors/theme.selector';
import { toSignal } from '@angular/core/rxjs-interop';
import { TopbarComponent } from '../../standalone/components/topbar/topbar.component';
import { FooterComponent } from '../../standalone/components/footer/footer.component';
import { LoadingIndicatorComponent } from '../../standalone/components/loading-indicator/loading-indicator.component';
import { GridOverlayComponent } from '../../standalone/components/grid-overlay/grid-overlay.component';
import { getAllLocationsWithOrderedDepartments, getLocationsLoaded } from '../../location/store';
import { FooterSocialsComponent } from '../../standalone/components/footer-socials/footer-socials.component';
import { getAllSocialChannels } from '../../social-media/store';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-website-layout',
  templateUrl: './website-layout.component.html',
  styleUrls: ['./website-layout.component.scss'],
  animations: [fade],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TopbarComponent,
    FooterComponent,
    LoadingIndicatorComponent,
    CookieNoticeComponent,
    FromDictionaryPipe,
    GridOverlayComponent,
    FromMenuPipe,
    FooterSocialsComponent,
  ],
})
export class WebsiteLayoutComponent {
  private store = inject(Store);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private viewportScroller = inject(ViewportScroller);
  private platformId = inject(PLATFORM_ID);
  private document: any = inject(DOCUMENT);
  private cookieConfig = inject(CookieConfigService);

  routeState$ = this.store.selectSignal(getRouterState);
  menuOpen$ = this.store.selectSignal(navSelectors.getMenuOpen);
  menuTheme$ = this.store.selectSignal(navSelectors.getMenuTheme);
  cookiePrefs$ = this.store.selectSignal(getCookiePreferences);
  theme$ = this.store.selectSignal(getTheme);
  dictionaryLoaded$ = this.store.selectSignal(getDictionaryLoaded);
  menuLoaded$ = this.store.selectSignal(getMenuLoaded);
  locations$ = this.store.selectSignal(getAllLocationsWithOrderedDepartments);
  locationsLoaded$ = this.store.selectSignal(getLocationsLoaded);
  socials$ = this.store.selectSignal(getAllSocialChannels);

  menuThemeEffect$ = effect(() => {
    const theme = this.menuTheme$();
  });

  private routerEvents$ = toSignal(
    this.router.events.pipe(
      filter(e => [NavigationStart, NavigationEnd, NavigationCancel, NavigationError, Scroll].some(b => e instanceof b))
    )
  );
  loading$ = computed(
    () =>
      !(
        this.routerEvents$() instanceof NavigationEnd ||
        this.routerEvents$() instanceof NavigationCancel ||
        this.routerEvents$() instanceof NavigationError ||
        this.routerEvents$() instanceof Scroll
      )
  );
  loaded$ = computed(() => {
    return this.dictionaryLoaded$() && this.menuLoaded$() && this.locationsLoaded$();
  });

  domainName = this.document.location.hostname;
  testGridEnabled = false;

  prevUrl: string | null = null;
  scrollToOffset = 100;

  animationState = signal<any>({});

  cookieUpdate = effect(() => {
    const cookiePrefs = this.cookiePrefs$();
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (cookiePrefs?.marketing) {
      this.loadPixel(environment.advertiserId);
      this.loadFacebookPixel();
    }
  });

  menuOpenEffect$ = effect(() => this.updateRoot(this.menuOpen$()));
  routerEffect$ = effect(() => {
    const event = this.routerEvents$();
    const state = this.routeState$();

    //console.log(event);

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (event instanceof NavigationStart && event.navigationTrigger === 'imperative' && !state?.state?.queryParams) {
      window.scrollTo(0, 0);
    }
    if (event instanceof NavigationEnd && this.cookiePrefs$()?.analytical) {
      if ((<any>window).gtag) {
        (<any>window).gtag('config', this.cookieConfig.analyticsCode, { page_path: event.urlAfterRedirects, anonymize_ip: true });
      }
    }
    if (isPlatformBrowser(this.platformId)) {
      this.document.querySelector('body').classList.add('set--in');
      this.document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    }

    if (event instanceof Scroll) {
      const urlFragment = event.anchor;
      // if we can smooth scroll to the fragment, do it. Otherwise fallback on build in anchor scrolling
      if (event.routerEvent.id > 1 && urlFragment && document.getElementById(urlFragment)) {
        this.smoothScroll(document.getElementById(urlFragment));
        return;
      }
      if (event.anchor) {
        // If anchor exists, scroll to anchor
        this.viewportScroller.scrollToAnchor(event.anchor);
      } else if (event.position) {
        // Scroll to the saved position
        this.viewportScroller.scrollToPosition(event.position);
      } else {
        // Scroll to the top
        this.viewportScroller.scrollToPosition([0, 0]);
      }
    }
  });

  private previousPath = '';

  constructor(viewport: ViewportScroller) {
    viewport.setOffset([0, this.scrollToOffset]);
  }

  toggleMenu() {
    this.store.dispatch(navActions.ToggleMenu());
  }

  closeMenu() {
    this.store.dispatch(navActions.CloseMenu());
  }

  openMenu() {
    this.store.dispatch(navActions.OpenMenu());
  }

  private updateRoot(menuActive: boolean) {
    menuActive
      ? this.renderer.addClass(this.document.body, 'main-nav--active')
      : this.renderer.removeClass(this.document.body, 'main-nav--active');
  }

  private smoothScroll(targetElement: HTMLElement | null): void {
    if (!targetElement) {
      return;
    }
    const yOffset = this.scrollToOffset * -1; // Your offset value
    const yPosition = targetElement.getBoundingClientRect().top + window.scrollY + yOffset;

    setTimeout(() => {
      window.scrollTo({
        top: yPosition,
        behavior: 'smooth',
      });
    }, 0);
  }

  loadPixel(advertiser_id: string) {
    if (advertiser_id) {
      const $script = document.createElement('script');
      $script.id = 'Pixel-Analytics';
      $script.async = true;
      $script.innerHTML = `(function() {
        setTimeout(function() {
          var adscienceScript = document.createElement("script");
          adscienceScript.type = "text/javascript";
          adscienceScript.async = true;
          adscienceScript.src = "//segments.optinadserving.com/segmentpixel.php?advertiser_id=${advertiser_id}";
          var s = document.getElementsByTagName("script")[0];
          s.parentNode.insertBefore(adscienceScript, s);
        },1);
      })(); `;

      const $scriptImage = document.createElement('noscript');
      $scriptImage.innerHTML = `<img src="//segments.optinadserving.com/cgi-bin/sgmnt.fcgi?advertiser_id=${advertiser_id}"/>`;

      document.head.prepend($script);
      document.head.prepend($scriptImage);
    }
  }

  private loadFacebookPixel() {
    if (typeof (<any>window)['fbq'] === 'undefined') {
      //console.log(portal.facebookPixelCode);
      const $script = document.createElement('script');
      $script.id = 'Facebook';
      $script.async = true;
      $script.innerHTML = `!function (f, b, e, v, n, t, s) {
            if (f.fbq) return; n = f.fbq = function () {
                n.callMethod ?
                    n.callMethod.apply(n, arguments) : n.queue.push(arguments)
            }; if (!f._fbq) f._fbq = n;
            n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = []; t = b.createElement(e); t.async = !0;
            t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s)
        }(window,
            document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '676149262853191');
        fbq('track', 'ViewContent');`;

      document.head.prepend($script);
    }
  }

  getRouteAnimationState(outlet: RouterOutlet) {
    // Only animate if url differs, not queryparams or fragment etc.
    if (outlet.activatedRouteData && outlet.isActivated) {
      const path = outlet.activatedRoute.snapshot.pathFromRoot
        .map(v => v.url.map(segment => segment.toString()))
        .flat()
        .join('/');
      return path;
    }
    return outlet.activatedRouteData;
  }
}
